import {OneSignalKey, OneSignalSafariWebId, SITE_URL} from 'config';
import {useRouter} from 'next/router';
import {useEffect, useRef} from 'react';
import OneSignalReact from 'react-onesignal';
import logger from 'utils/logger';

import {useTranslation} from './useTranslation';
declare global {
  interface Window {
    OneSignal: typeof OneSignalReact | undefined;
  }
}

const useOneSignal = () => {
  const {t} = useTranslation();
  const onesignalInitializingRef = useRef(false);
  const {replace} = useRouter();

  // onesignal cannot work on local host & prod at the same time
  /**
   * ConfigHelper.js:52 Uncaught (in promise) f: OneSignal: This web push config can only be used on https://www.straus.md. Your current origin is http://localhost:8080.
   */
  const init = async () => {
    try {
      if (onesignalInitializingRef.current) return;
      logger.logDebug('Initializing OneSignal', 'useOneSignal');
      if (!window?.OneSignal) return;
      await window?.OneSignal?.init({
        appId: OneSignalKey,
        safari_web_id: OneSignalSafariWebId,
        notifyButton: {
          enable: false,
        },
        allowLocalhostAsSecureOrigin: true,
        // https://documentation.onesignal.com/docs/web-push-options#triggering-notification-persistence-on-chrome
        persistNotification: true,
        promptOptions: {
          actionMessage: t('notificationTitle'),
          acceptButton: t('yes'),
          cancelButton: t('no'),
          // change request from @alex -maketolog
          slidedown: {
            enabled: true,
            autoPrompt: true,
            timeDelay: 10,
            pageViews: 2,
          },
        },
        welcomeNotification: {
          title: t('welcomeTitle'),
          message: t('welcomeMessage'),
        },
      });

      OneSignalReact.addListenerForNotificationOpened((notification) => {
        logger.addBreadcrumb('Notification Opened', {notification});
        logger.logDebug(`Notification Opened: ${notification}`);

        if (!notification.data) return;
        if (notification?.data?.url) return (window.location.href = notification.data.url);
        if (notification?.data?.type === 'complete_order') return replace('/cart');
        if (notification?.data?.type !== 'status_monitoring') return;
        if (!notification.data.type) return;
        window.location.href = `${SITE_URL}/order/${notification.data.order_uuid}`;
      });
    } catch (e: any) {
      logger.error(e);
    } finally {
      onesignalInitializingRef.current = true;
    }
  };

  useEffect(() => {
    init();

    return () => {
      // https://onesignal.com/blog/how-to-integrate-onesignal-into-a-next-app/
      // dublicate initialization
      window.OneSignal = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
export default useOneSignal;
